import { Component } from '@verndale/core';

type Publication = {
  title: string;
  pubdate: string;
  authors: { name: string }[];
  source: string;
};

interface SearchData {
  esearchresult: {
    count: string;
    idlist: string[];
  };
}

interface SummaryData {
  result: {
    uids: string[];
    [uid: string]: Publication | string[];
  };
}

const ENDPOINT_CONFIG = {
  database: 'db=pubmed',
  retmode: '&retmode=json',
  retmax: 8,
  sortby: '&sort=pub_date',
  rettype: '&rettype=abstract',
  linkUrl: 'http://www.ncbi.nlm.nih.gov/pubmed/',
};

class PublicationsRow extends Component {
  private allArticleIDs: string[] = [];
  private currentOffset = 0;
  private totalArticles = 0;

  setupDefaults() {
    this.dom = {
      showBtn: this.el.querySelector('.publications-row__show-more') as HTMLElement,
      publicationsList: this.el.querySelector('.publications-row__publication-item-list') as HTMLElement | null,
    };

    (this.dom.showBtn as HTMLElement).style.display = 'none';

    this.fetchPublications();
  }

  addListeners() {
    (this.dom.showBtn as HTMLElement).addEventListener('click', this.handleShowMore.bind(this));
  }

  async fetchPublications() {
    if (!this.el.dataset.endpointsearch || !this.el.dataset.searchterm) {
      console.error('Missing search endpoint or search term.');
      return;
    }

    const searchURL = this.buildSearchURL(ENDPOINT_CONFIG.retmax);

    try {
      const searchData = await this.fetchData<SearchData>(searchURL);
      this.totalArticles = parseInt(searchData.esearchresult.count, 10);
      this.allArticleIDs = searchData.esearchresult.idlist;

      if (this.allArticleIDs.length > 0) {
        await this.fetchPublicationDetails(this.allArticleIDs.join(','));
      }
    } catch (error) {
      console.error('Error fetching publications:', error);
    }
  }

  async handleShowMore() {
    const showBtn = this.dom.showBtn as HTMLElement;
    if (this.currentOffset >= this.totalArticles) {
      showBtn.style.display = 'none';
      return;
    }

    if (this.allArticleIDs.length <= this.currentOffset) {
      const searchURL = this.buildSearchURL(this.totalArticles);
      try {
        const searchData = await this.fetchData<SearchData>(searchURL);
        this.allArticleIDs = searchData.esearchresult.idlist;
      } catch (error) {
        console.error('Error fetching more article IDs:', error);
        return;
      }
    }

    const nextBatchIDs = this.allArticleIDs.slice(this.currentOffset, this.currentOffset + ENDPOINT_CONFIG.retmax).join(',');
    await this.fetchPublicationDetails(nextBatchIDs);
  }

  buildSearchURL(retmax: number): string {
    const { endpointsearch, searchterm } = this.el.dataset;
    return `${endpointsearch}${ENDPOINT_CONFIG.database}${ENDPOINT_CONFIG.retmode}&retmax=${retmax}${ENDPOINT_CONFIG.sortby}${searchterm}`;
  }

  buildSummaryURL(idList: string): string {
    const { endpointsummary } = this.el.dataset;
    return `${endpointsummary}${ENDPOINT_CONFIG.database}${ENDPOINT_CONFIG.retmode}${ENDPOINT_CONFIG.rettype}&id=${idList}`;
  }

  async fetchData<T>(url: string): Promise<T> {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`API returned status ${response.status}`);
    }
    return (await response.json()) as T;
  }

  async fetchPublicationDetails(idList: string) {
    if (!this.el.dataset.endpointsummary) {
      console.error('Missing summary endpoint.');
      return;
    }

    const summaryURL = this.buildSummaryURL(idList);

    try {
      const summaryData = await this.fetchData<SummaryData>(summaryURL);
      this.displayPublications(summaryData.result);
    } catch (error) {
      console.error('Error fetching publication details:', error);
    }
  }

  displayPublications(publicationsData: Record<string, Publication | string[]>) {
    const publicationsList = this.dom.publicationsList as HTMLElement;

    const nextBatchIDs = this.allArticleIDs.slice(this.currentOffset, this.currentOffset + ENDPOINT_CONFIG.retmax);
    nextBatchIDs.forEach((uid: string) => {
      const publication = publicationsData[uid] as Publication;

      if (publication) {
        const publicationHTML = this.buildPublicationHTML(uid, publication);
        publicationsList.insertAdjacentHTML('beforeend', publicationHTML);
      }
    });

    this.currentOffset += ENDPOINT_CONFIG.retmax;
    this.updateShowMoreBtn();
  }

  buildPublicationHTML(uid: string, publication: Publication): string {
    const title = publication.title || '';
    const pubDate = publication.pubdate || '';
    const authors = publication.authors.map(author => author.name).join(', ');
    const pubmedLink = `${ENDPOINT_CONFIG.linkUrl}${uid}`;
    const fullJournalName = publication.source || '';

    return `
      <li class="publications-row__publication-item">
        <p class="publications-row__publication-item-year">${pubDate.slice(0, 4)}</p>
        <a href="${pubmedLink}" class="publications-row__link" target="_blank">
          ${title}
        </a>
        <p class="publications-row__publication-item-subtitle">${fullJournalName}</p>
        <p class="publications-row__publication-item-authors">${authors}</p>
      </li>
    `;
  }

  updateShowMoreBtn() {
    const showBtn = this.dom.showBtn as HTMLElement;
    showBtn.style.display = this.currentOffset < this.totalArticles ? 'block' : 'none';
  }
}

export default PublicationsRow;
